import {
  ActionError,
  AttentionBlock,
  Button,
  ConfirmDialog,
  Field,
  PageHeader,
  PageTitle,
  Skeleton,
  StatusTag,
  Surface,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  formatDate,
  formatTime,
  titleCase,
  toast,
  useBreakpoint,
} from '@westhetics/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { GuardedLink, UnsavedDialog } from '@/features/patients/unsaved-guard'
import { LeaveContext, useUnsavedGuard } from '@/features/patients/use-unsaved-guard'
import { saveDownloadedFile } from '@/features/signatures/api'
import { isApiError } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import {
  putPlanInCache,
  useAddProduct,
  useArchiveTreatmentPlan,
  useCloseTreatmentPlan,
  useRestoreTreatmentPlan,
  useSendTreatmentPlanEmail,
  useTreatmentPlan,
  useTreatmentPlanPdf,
  useUpdateTreatmentPlan,
  type Product,
  type Stage,
  type TreatmentPlan,
  type TreatmentPlanInput,
} from './api'
import { CloseDialog } from './close-dialog'
import { GeneralTab } from './general-tab'
import {
  EMAIL_RECIPIENT_LABELS,
  EMAIL_STATUS_LABELS,
  IMAGE_CONSENT_LABELS,
  MSG_CLOSED,
  MSG_PRODUCT_ADDED,
  STAGE_LABELS,
  STATUS_LABELS,
  STATUS_LEVELS,
  archivedLine,
  planSummary,
  productName,
  stagesWithoutPhoto,
} from './labels'
import { PhotosTab } from './photos-tab'
import { PlanMobile } from './plan-mobile'
import { ProductCard } from './product-card'
import { QrDialog, type QrTarget } from './qr-dialog'
import { useAutosave } from './use-autosave'

/**
 * A ficha do plano (docs/areas/planos-de-tratamento.md, "Ficha do plano"; RN-PLA-04 a 33).
 * Arquétipo: ficha com abas. Informações gerais gravam sozinhas; Produtos e Fotos são a prova.
 * Encerrar, arquivar e restaurar só para quem pode (RT-02); plano de clínica irmã é só leitura
 * (RN-PLA-34); abaixo de 900px a ficha abre em leitura com "Fotografar" por etapa (RN-PLA-35).
 */

type Tab = 'info' | 'products' | 'photos'
const TABS: Tab[] = ['info', 'products', 'photos']

export function TreatmentPlanPage() {
  const { id } = useParams()
  const query = useTreatmentPlan(id)
  const plan = query.data

  if (!plan) {
    return (
      <>
        <PageHeader
          LinkComponent={Link}
          crumbs={[{ label: 'Planos de Tratamento', href: routes.treatmentPlans }, { label: '…' }]}
          actions={
            <Button variant="outline" size="sm" asChild>
              <Link to={routes.treatmentPlans}>Voltar</Link>
            </Button>
          }
        />
        <Page wide>
          {query.isError ? (
            <LoadError
              title="Não conseguimos abrir este plano"
              error={query.error}
              onRetry={() => void query.refetch()}
            />
          ) : (
            <div role="status" aria-label="Carregando" className="flex flex-col gap-4">
              <Skeleton className="h-[26px] w-[320px] max-w-full" />
              <Skeleton className="h-[13px] w-[460px] max-w-full" />
              <Skeleton className="mt-4 h-[13px] w-[380px] max-w-full" />
              <Skeleton className="h-[280px] w-full" />
            </div>
          )}
        </Page>
      </>
    )
  }
  return <PlanRecord key={plan.id} plan={plan} />
}

function PlanRecord({ plan }: { plan: TreatmentPlan }) {
  const session = useSession()
  const api = useApi()
  const queryClient = useQueryClient()
  const isDesk = useBreakpoint('desk')
  const [params, setParams] = useSearchParams()
  const reasonId = useId()
  const tab: Tab = TABS.includes(params.get('tab') as Tab) ? (params.get('tab') as Tab) : 'info'

  const update = useUpdateTreatmentPlan(plan.id)
  const close = useCloseTreatmentPlan(plan.id)
  const archive = useArchiveTreatmentPlan(plan.id)
  const restore = useRestoreTreatmentPlan(plan.id)
  const sendEmail = useSendTreatmentPlanEmail(plan.id)
  const pdf = useTreatmentPlanPdf()
  const addProduct = useAddProduct(plan.id)

  const [closing, setClosing] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [reason, setReason] = useState('')
  const [reasonAsked, setReasonAsked] = useState(false)
  const [qr, setQr] = useState<{ target: QrTarget; title: string } | null>(null)
  const [failure, setFailure] = useState<{ title: string; error: unknown } | null>(null)
  const [closeFields, setCloseFields] = useState<Record<string, string>>({})
  const [highlightedProduct, setHighlightedProduct] = useState<string | null>(null)

  const role = session.profile?.role
  const ownPractitionerId =
    session.profile?.clinics.find((c) => c.id === session.profile?.clinicId)?.practitionerId ?? null
  const isOwn = ownPractitionerId !== null && ownPractitionerId === plan.practitioner.id
  const canDecide = session.canManageClinic || (role === 'practitioner' && isOwn)
  const isNetwork = plan.clinic !== null
  const readOnly = isNetwork || plan.status !== 'open' || !isDesk
  const displayName = titleCase(plan.patient.name)

  const autosave = useAutosave<TreatmentPlanInput>({
    version: plan.version,
    save: async (input, version) => {
      const saved = await update.mutateAsync({ input, version })
      return { version: saved.version }
    },
    reload: async () => {
      const fresh = await api.get<TreatmentPlan>(`/treatment-plans/${plan.id}`)
      putPlanInCache(queryClient, fresh)
      return fresh.version
    },
  })
  const guard = useUnsavedGuard(autosave.dirty || autosave.status === 'saving')

  function setTab(next: Tab) {
    const nextParams = new URLSearchParams(params)
    nextParams.set('tab', next)
    setParams(nextParams, { replace: true })
  }

  function doClose() {
    setFailure(null)
    const missing = stagesWithoutPhoto(plan)
    close.mutate(
      { version: plan.version, hasConfirmedIncompleteStages: missing.length > 0 || undefined },
      {
        onSuccess: () => {
          setClosing(false)
          setCloseFields({})
          toast(MSG_CLOSED)
        },
        onError: (error) => {
          const fields = fieldErrors(error)
          if (Object.keys(fields).length > 0) {
            setCloseFields(fields)
            const productKey = Object.keys(fields).find((key) => key.startsWith('products'))
            if (productKey) {
              setClosing(false)
              setTab('products')
              const index = /^products\[(\d+)\]/.exec(productKey)?.[1]
              setHighlightedProduct(
                index !== undefined ? (plan.products[Number(index)]?.id ?? null) : null,
              )
            }
            return
          }
          setClosing(false)
          setFailure({ title: 'Não foi possível encerrar o plano', error })
        },
      },
    )
  }

  function doArchive() {
    setFailure(null)
    archive.mutate(
      { version: plan.version, reason: plan.status === 'closed' ? reason.trim() : undefined },
      {
        onSuccess: (archived) => {
          setArchiving(false)
          setReason('')
          toast('Plano arquivado', {
            undo: () =>
              restore.mutate(
                { version: archived.version },
                { onError: (error) => toast(errorMessage(error)) },
              ),
          })
        },
        onError: (error) => {
          setArchiving(false)
          setFailure({ title: 'Não foi possível arquivar o plano', error })
        },
      },
    )
  }

  function doRestore() {
    setFailure(null)
    restore.mutate(
      { version: plan.version },
      {
        onSuccess: () => toast('Plano restaurado.'),
        onError: (error) => setFailure({ title: 'Não foi possível restaurar o plano', error }),
      },
    )
  }

  function downloadPdf() {
    pdf.mutate(plan.id, {
      onSuccess: (file) =>
        saveDownloadedFile(file, plan.pdf?.fileName ?? 'plano-de-tratamento.pdf'),
      onError: (error) => toast(errorMessage(error)),
    })
  }

  function resend() {
    sendEmail.mutate(undefined, {
      onSuccess: (sent) => toast(`E-mail a caminho de ${sent.address}.`),
      onError: (error) => toast(errorMessage(error)),
    })
  }

  function newProduct() {
    if (plan.products.length >= 20) {
      toast('Este plano já tem 20 produtos, o máximo.')
      return
    }
    addProduct.mutate(
      {},
      {
        onSuccess: () => toast(MSG_PRODUCT_ADDED),
        onError: (error) => toast(errorMessage(error)),
      },
    )
  }

  function openQrForProduct(product: Product) {
    setQr({
      target: { target: 'product', productId: product.id },
      title: `${productName(product)}`,
    })
  }

  function openQrForStage(stage: Stage) {
    setQr({ target: { target: 'stage', stage }, title: STAGE_LABELS[stage].title })
  }

  /** Os erros do 422 do encerramento para o cartão de índice `index`, sem o prefixo. */
  function errorsForProduct(index: number): Record<string, string> {
    const prefix = `products[${index}].`
    const out: Record<string, string> = {}
    for (const [key, message] of Object.entries(closeFields)) {
      if (key.startsWith(prefix)) out[key.slice(prefix.length)] = message
    }
    return out
  }

  const consentNote = plan.imageConsent !== 'yes' ? IMAGE_CONSENT_LABELS[plan.imageConsent] : null
  const reasonTooShort =
    plan.status === 'closed' &&
    reason.trim().length < 10 &&
    (reasonAsked || reason.trim().length > 0)

  const statusTag = (
    <StatusTag
      level={STATUS_LEVELS[plan.status]}
      className={plan.status === 'closed' ? 'bg-ink text-pampas' : undefined}
    >
      {STATUS_LABELS[plan.status]}
    </StatusTag>
  )

  const pdfButton =
    plan.pdf && (plan.status === 'closed' || plan.previousStatus === 'closed') ? (
      <Button
        variant="outline"
        size="sm"
        loading={pdf.isPending}
        loadingLabel="Preparando…"
        onClick={downloadPdf}
      >
        Baixar PDF
      </Button>
    ) : null

  const actionCards = (
    <>
      {plan.status === 'closed' && plan.evidence ? (
        <Surface className="max-desk:px-4 flex flex-col gap-3" aria-label="Registro de prova">
          <div className="font-display text-lead text-ink leading-[1.3] font-normal">
            Registro de prova
          </div>
          <p className="font-body text-secondary text-[12px] leading-[1.6] font-light">
            Encerrado por {plan.closedBy ? titleCase(plan.closedBy.name) : '—'} em{' '}
            {plan.closedAt
              ? `${formatDate(new Date(plan.closedAt))} · ${formatTime(new Date(plan.closedAt))}`
              : '—'}{' '}
            · Protocolo{' '}
            <span className="font-display text-ink">{plan.evidence.protocolNumber}</span> · Hash{' '}
            <span className="font-display text-ink">
              {plan.evidence.hash.slice(0, 8)}…{plan.evidence.hash.slice(-6)}
            </span>
          </p>
          <ul
            className="font-body text-secondary flex flex-col gap-1 text-[12px] leading-[1.6] font-light"
            aria-label="E-mails"
          >
            {plan.emails.map((email) => (
              <li key={`${email.recipient}-${email.address ?? ''}`}>
                {EMAIL_RECIPIENT_LABELS[email.recipient]} ·{' '}
                {email.status === 'not_sent' && !email.address
                  ? 'a paciente não tem e-mail no cadastro'
                  : EMAIL_STATUS_LABELS[email.status]}
                {email.address ? ` · ${email.address}` : ''}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-3">
            {pdfButton}
            <Button
              variant="text"
              size="sm"
              loading={sendEmail.isPending}
              loadingLabel="Reenviando…"
              onClick={resend}
            >
              Reenviar por e-mail
            </Button>
          </div>
        </Surface>
      ) : null}

      {plan.status === 'open' && canDecide && !isNetwork ? (
        <Surface className="max-desk:px-4 flex flex-wrap items-center justify-between gap-4">
          <div className="max-w-[520px]">
            <div className="font-display text-lead text-ink leading-[1.3] font-normal">
              Encerrar plano
            </div>
            <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
              Vira registro de prova com protocolo e hash, gera o PDF e envia a cópia. Exige cada
              produto com lote, validade e as três fotos.
            </p>
          </div>
          <Button variant="outline" onClick={() => setClosing(true)}>
            Encerrar plano
          </Button>
        </Surface>
      ) : null}

      {plan.status !== 'archived' && canDecide && !isNetwork ? (
        <Surface className="max-desk:px-4 flex flex-wrap items-center justify-between gap-4">
          <div className="max-w-[520px]">
            <div className="font-display text-lead text-ink leading-[1.3] font-normal">
              Arquivar plano
            </div>
            <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
              Sai das listas e do dashboard. Produtos, fotos e o atendimento continuam guardados e o
              plano pode voltar.
              {plan.status === 'closed' ? ' Plano encerrado pede o motivo.' : ''}
            </p>
          </div>
          <Button variant="outline" onClick={() => setArchiving(true)}>
            Arquivar
          </Button>
        </Surface>
      ) : null}

      {plan.status === 'archived' ? (
        <Surface className="max-desk:px-4 flex flex-wrap items-center justify-between gap-4">
          <div className="max-w-[520px]">
            <div className="font-display text-lead text-ink leading-[1.3] font-normal">
              {archivedLine(plan)}
            </div>
            <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
              Continua legível por id{plan.pdf ? ', com o PDF' : ''}. Restaurar devolve ao estado
              anterior.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {pdfButton}
            {canDecide ? (
              <Button
                variant="outline"
                loading={restore.isPending}
                loadingLabel="Restaurando…"
                onClick={doRestore}
              >
                Restaurar
              </Button>
            ) : null}
          </div>
        </Surface>
      ) : null}
    </>
  )

  return (
    <>
      <LeaveContext.Provider value={guard.leave}>
        <PageHeader
          LinkComponent={GuardedLink}
          crumbs={[
            { label: 'Planos de Tratamento', href: routes.treatmentPlans },
            { label: displayName },
          ]}
          actions={
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => guard.leave(routes.treatmentPlans)}
              >
                Voltar
              </Button>
              {isDesk && plan.status === 'closed' ? pdfButton : null}
            </>
          }
        />
      </LeaveContext.Provider>
      <Page wide>
        <PageTitle
          eyebrow={isDesk ? undefined : 'Plano de tratamento'}
          title={displayName}
          meta={planSummary(plan)}
          trailing={statusTag}
        />

        {plan.warnings.map((warning) => (
          <AttentionBlock
            key={warning.code}
            className="mt-4"
            variant="plum"
            title="Documentos do atendimento cancelados"
          >
            {warning.message}
          </AttentionBlock>
        ))}
        {isNetwork ? (
          <AttentionBlock className="mt-4" variant="lilac">
            Plano da clínica {plan.clinic?.name}, da sua rede: só leitura.
          </AttentionBlock>
        ) : null}
        {failure ? (
          <ActionError
            className="mt-4"
            title={failure.title}
            description={`${errorMessage(failure.error)}${isApiError(failure.error) && failure.error.code === 'unavailable' ? '' : ' Nada mudou no plano.'}`}
            onDismiss={() => setFailure(null)}
          />
        ) : null}

        {isDesk ? (
          <Tabs className="mt-6" value={tab} onValueChange={(value) => setTab(value as Tab)}>
            <TabsList>
              <TabsTrigger value="info">Informações gerais</TabsTrigger>
              <TabsTrigger value="products">
                Produtos{plan.products.length ? ` ${plan.products.length}` : ''}
              </TabsTrigger>
              <TabsTrigger value="photos">
                Fotos {plan.recordedStageCount} de 4
                {consentNote ? <span className="text-label"> · {consentNote}</span> : null}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="info">
              <GeneralTab
                plan={plan}
                autosave={autosave}
                readOnly={readOnly}
                errors={update.isError ? fieldErrors(update.error) : {}}
              >
                {actionCards}
              </GeneralTab>
            </TabsContent>
            <TabsContent value="products">
              <div className="flex flex-col gap-4">
                {closeFields.products ? (
                  <ActionError
                    title="O encerramento precisa de produto"
                    description={closeFields.products}
                    onDismiss={() => setCloseFields({})}
                  />
                ) : null}
                {plan.products.length === 0 ? (
                  <Surface className="max-desk:px-4 flex flex-col items-center gap-3 py-10 text-center">
                    <div className="font-display text-name text-ink leading-[1.3] font-normal">
                      Nenhum produto registrado
                    </div>
                    <p className="font-body text-body text-secondary max-w-[420px] font-light">
                      Cada produto aplicado entra com lote fotografado, medida e regiões — é a
                      rastreabilidade exigida em caso de intercorrência.
                    </p>
                    {!readOnly ? (
                      <Button
                        variant="outline"
                        loading={addProduct.isPending}
                        loadingLabel="Adicionando…"
                        onClick={newProduct}
                      >
                        + Adicionar produto
                      </Button>
                    ) : null}
                  </Surface>
                ) : (
                  plan.products.map((product, index) => (
                    <ProductCard
                      key={product.id}
                      plan={plan}
                      product={product}
                      readOnly={readOnly}
                      errors={errorsForProduct(index)}
                      highlighted={highlightedProduct === product.id}
                      onOpenQr={openQrForProduct}
                    />
                  ))
                )}
                {!readOnly && plan.products.length > 0 ? (
                  <div>
                    <Button
                      variant="outline"
                      loading={addProduct.isPending}
                      loadingLabel="Adicionando…"
                      onClick={newProduct}
                    >
                      + Adicionar produto
                    </Button>
                  </div>
                ) : null}
              </div>
            </TabsContent>
            <TabsContent value="photos">
              <PhotosTab plan={plan} readOnly={readOnly} onOpenQr={openQrForStage} />
            </TabsContent>
          </Tabs>
        ) : (
          <div className="mt-6">
            <PlanMobile
              plan={plan}
              readOnly={isNetwork || plan.status !== 'open'}
              actions={actionCards}
            />
          </div>
        )}
      </Page>

      <CloseDialog
        plan={plan}
        open={closing}
        onOpenChange={setClosing}
        onConfirm={doClose}
        loading={close.isPending}
        stagesError={closeFields.stages ?? null}
      />

      <ConfirmDialog
        open={archiving}
        onOpenChange={(open) => {
          if (!archive.isPending) setArchiving(open)
        }}
        tone="primary"
        title={`Arquivar o plano de ${displayName}?`}
        effect="Ele sai das listas e do dashboard. Produtos, fotos e o atendimento continuam guardados e o plano pode voltar."
        confirmLabel="Arquivar"
        loading={archive.isPending}
        loadingLabel="Arquivando…"
        onConfirm={() => {
          if (plan.status === 'closed' && reason.trim().length < 10) {
            setReasonAsked(true)
            return
          }
          doArchive()
        }}
      >
        {plan.status === 'closed' ? (
          <Field
            id={reasonId}
            label="Motivo"
            hint="Vai para a trilha. De 10 a 500 caracteres."
            error={
              reasonTooShort
                ? 'Diga por que este plano encerrado está sendo arquivado, em 10 a 500 caracteres.'
                : null
            }
          >
            <Textarea
              id={reasonId}
              rows={3}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        ) : null}
      </ConfirmDialog>

      {qr ? (
        <QrDialog plan={plan} target={qr.target} title={qr.title} onClose={() => setQr(null)} />
      ) : null}

      <UnsavedDialog
        open={guard.isAsking}
        blocks={['Informações gerais']}
        kept="O que já foi salvo continua."
        onStay={guard.stay}
        onLeave={guard.confirm}
      />
    </>
  )
}
